import React, { useState, useEffect, useRef } from 'react';
import { FiUser, FiPhone, FiLogOut, FiImage, FiEdit, FiBriefcase, FiPercent, FiArrowLeft, FiMail } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { database, ref, get, storage, storageRef, getDownloadURL } from '../firebase';
import { FaRupeeSign } from 'react-icons/fa';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [allUsersPoints, setAllUsersPoints] = useState([]); // NEW STATE
  const [currentIndex, setCurrentIndex] = useState(0); // NEW STATE
  const sliderRef = useRef(null); // NEW REF
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      // Fetch user from localStorage
      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      if (!currentUser) {
        setError('Please log in to view your profile.');
        setIsLoading(false);
        navigate('/login');
        return;
      }

      try {
        let dbUserData = null;
        
        // Try to find user by mobile number first (primary login method)
        if (currentUser.mobile) {
          const userRef = ref(database, `videoplayer/Users/${currentUser.mobile}`);
          const snapshot = await get(userRef);
          if (snapshot.exists()) {
            dbUserData = snapshot.val();
          }
        }
        
        // If not found by mobile, try to find by searching all users
        if (!dbUserData) {
          const usersRef = ref(database, 'videoplayer/Users');
          const snapshot = await get(usersRef);
          if (snapshot.exists()) {
            const usersData = snapshot.val();
            // Search for user by email
            const foundUser = Object.entries(usersData).find(([id, user]) => 
              user.email === currentUser.email || user.mobile === currentUser.mobile
            );
            if (foundUser) {
              dbUserData = foundUser[1];
            }
          }
        }

        if (dbUserData) {
          // Update localStorage with latest data from Firebase
          const updatedUser = {
            name: dbUserData.name || dbUserData.fullName || null,
            surname: dbUserData.surname || null,
            email: dbUserData.email || currentUser.email || null,
            mobile: dbUserData.mobile || currentUser.mobile || null,
            plan: dbUserData.plan || null,
            business: dbUserData.business || null,
            gst: dbUserData.gst || null,
            phoneNumber: dbUserData.phoneNumber || dbUserData.mobile || currentUser.mobile,
            termsAccepted: dbUserData.termsAccepted || false,
            profilePictureUrl: dbUserData.profilePictureUrl || null,
          };
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          setUserData(updatedUser);

          // Only set profile image if profilePictureUrl exists
          if (dbUserData.profilePictureUrl) {
            setProfileImage(dbUserData.profilePictureUrl);
          } else {
            setProfileImage(null);
          }
        } else {
          // If not found in database, use localStorage data
          setUserData(currentUser);
        }
      } catch (err) {
        console.error('Firebase Error:', err);
        setError('An error occurred while fetching your profile. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch all users' points for the slider
    const fetchAllUsersPoints = async () => {
      try {
        const usersRef = ref(database, 'videoplayer/Users');
        const snapshot = await get(usersRef);
        if (snapshot.exists()) {
          const usersData = snapshot.val();
          const usersArray = Object.entries(usersData).map(([id, user]) => ({
            mobile: user.mobile || user.phoneNumber || id,
            points: user.points || 0,
            time: user.timestamp || user.createdAt || user.registered || user.time || 'N/A',
          }));
          setAllUsersPoints(usersArray);
        } else {
          setAllUsersPoints([]);
        }
      } catch (err) {
        setAllUsersPoints([]);
      }
    };

    fetchUserData();
    fetchAllUsersPoints();
  }, [navigate]);

  // Auto-scroll effect
  useEffect(() => {
    if (allUsersPoints.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % allUsersPoints.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [allUsersPoints]);

  // Scroll to current index
  useEffect(() => {
    if (!sliderRef.current || allUsersPoints.length === 0) return;
    const child = sliderRef.current.children[currentIndex];
    if (child) {
      child.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentIndex, allUsersPoints]);

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    alert('Logged out successfully.');
    navigate('/login');
  };

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  const handleGoBack = () => {
    navigate('/home'); 
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-200 flex items-center justify-center">
        <div className="flex items-center space-x-2 text-gray-600">
          <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-200 flex items-center justify-center">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm border border-red-200 max-w-md">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-200 flex flex-col items-center justify-center p-4 sm:p-6">
      {/* Removed Slider for all users' points */}
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md relative">
        {/* Back Button */}
        <button
          onClick={handleGoBack}
          className="absolute top-4 left-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-2 transition-all duration-200 hover:bg-gray-100"
          aria-label="Go back"
        >
          <FiArrowLeft className="text-xl" />
        </button>

        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-6 text-center flex items-center justify-center">
          <FiUser className="mr-3 text-blue-600 text-3xl" /> Your Profile
        </h2>
        
        {/* Profile Image Display */}
        {profileImage && (
          <div className="flex justify-center mb-6">
            <img
              src={profileImage}
              alt="Profile"
              className="w-24 h-24 rounded-full object-cover shadow-lg"
            />
          </div>
        )}
        
        <div className="flex flex-col items-center space-y-6">
          <div className="w-full space-y-4">
            <div className="flex items-center space-x-2">
              <FiUser className="text-blue-600 text-xl" />
              <div>
                <p className="text-sm font-medium text-gray-700">Name</p>
                <p className="text-gray-900">{userData.name || userData.fullName || 'N/A'}</p>
              </div>
            </div>
            {userData.surname && (
              <div className="flex items-center space-x-2">
                <FiUser className="text-blue-600 text-xl" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Surname</p>
                  <p className="text-gray-900">{userData.surname}</p>
                </div>
              </div>
            )}
            {userData.email && (
              <div className="flex items-center space-x-2">
                <FiMail className="text-blue-600 text-xl" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Email</p>
                  <p className="text-gray-900">{userData.email}</p>
                </div>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <FiPhone className="text-blue-600 text-xl" />
              <div>
                <p className="text-sm font-medium text-gray-700">Mobile Number</p>
                <p className="text-gray-900">{userData.mobile || userData.phoneNumber || 'N/A'}</p>
              </div>
            </div>
            {userData.plan && (
              <div className="flex items-center space-x-2">
                <FaRupeeSign className="text-blue-600 text-xl" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Plan</p>
                  <p className="text-gray-900">{userData.plan}</p>
                </div>
              </div>
            )}
            {userData.business && (
              <div className="flex items-center space-x-2">
                <FiBriefcase className="text-blue-600 text-xl" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Business</p>
                  <p className="text-gray-900">{userData.business}</p>
                </div>
              </div>
            )}
            {userData.gst && (
              <div className="flex items-center space-x-2">
                <FiPercent className="text-blue-600 text-xl" />
                <div>
                  <p className="text-sm font-medium text-gray-700">GST</p>
                  <p className="text-gray-900">{userData.gst}%</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex space-x-4 w-full">
            <button
              onClick={handleLogout}
              className="w-1/2 bg-red-600 text-white p-3 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-300 flex items-center justify-center"
            >
              <FiLogOut className="mr-2" /> Log Out
            </button>
            <button
              onClick={handleEditProfile}
              className="w-1/2 bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 flex items-center justify-center"
            >
              <FiEdit className="mr-2" /> Edit Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;